import AddCommentIcon from "@mui/icons-material/AddComment";
import ResolveIcon from "@mui/icons-material/AssignmentTurnedIn";
import ForumIcon from "@mui/icons-material/Forum";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, SxProps, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import EmptyInboxPlaceholder from "app/assets/empty_inbox_placeholder.svg";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import { NotAuthorized } from "common/components/not-authorized";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import {
    createMRTColumnHelper,
    MaterialReactTable,
    MRT_TableInstance as TableInstance,
    MRT_ShowHideColumnsButton as ShowHideColumnsButton,
    MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
    MRT_ToggleFiltersButton as ToggleFiltersButton,
    MRT_ToggleFullScreenButton as ToggleFullScreenButton,
    MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
    useMaterialReactTable,
    type MRT_ColumnFiltersState as ColumnFiltersState,
    type MRT_PaginationState as PaginationState,
} from "material-react-table";
import QueryForumsApiRequest, {
    ForumFilterParameters,
    ForumOrderField,
    ForumOrderParameters
} from "messaging/entities/forum/api/request-contracts/query-forums-api-request";
import Forum from "messaging/entities/forum/forum";

import {
    getErrorLoadingForumsByQuery,
    getForumQueryByInterface,
    getForumsByQuery, getIsLoadingForumsByQuery,
    populateForumsByQuery,
    updateForumInterfaceQuery
} from "messaging/entities/forum/store/forums-redux-slice";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "users/session/session-context";


const NoRowsContainer = styled("div")(
    () => ({
        display: "flex",
        justifyContent: "center",
    }));
const RowActionsContainer = styled(Box)(
    () => ({
        display: "flex",
        flexWrap: "nowrap",
        width: "inherit",
    }));
const ToolbarButtonsContainer = styled("div")(
    ({ theme }) => ({
        [theme.breakpoints.down("md")]: {
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
        },
        display: "flex",
        alignItems: "end",
        flexDirection: "column",
        gap: theme.spacing(1),
    }));
const ToolbarButtons = styled("div")(
    () => ({
        display: "flex",
        alignItems: "end",
        flexWrap: "nowrap",
    }));
const ToolbarActionButtons = styled("div")(
    () => ({
        textAlign: "right",
        whiteSpace: "nowrap",
    }));
const CategoryItem = styled(ListItem)(
    () => ({
        margin: 0,
        padding: 0,
    }));
const AlertActions = styled("div")(
    ({ theme }) => ({
        alignItems: "center",
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "nowrap",
        height: "100%",
        paddingRight: theme.spacing(2),
        whiteSpace: "nowrap",
    }));
const AllCaughtUp = styled("img")(
    ({ theme }) => ({
        maxHeight: "100px",
        padding: theme.spacing(2),
    }));
const NewForumButton = styled(IconButton)(
    ({ theme }) => ({
        padding: 0,
        paddingRight: theme.spacing(1),
    }));
const ComposeMessageButton = styled(IconButton)(
    ({ theme }) => ({
        padding: 0,
        paddingRight: theme.spacing(1),
    }));

type ConversationTableProps = {
    renderTopToolbarCustomActions: (props: { table: TableInstance<Forum> }) => React.ReactNode;
};

export default function ConversationTable(props: Readonly<ConversationTableProps>) {
    const { renderTopToolbarCustomActions } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [showAlertBanner, setShowAlertBanner] = useState(false);
    const [showSkeletons, setShowSkeletons] = useState(false);
    const [showProgressBars, setShowProgressBars] = useState(false);
    const [rowSelection, setRowSelection] = React.useState<
        Record<string, boolean>
    >({});

    const [globalFilter, setGlobalFilter] = useState("");
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        []
    );
    const [sorting, setSorting] = useState<Array<{ id: string, desc: boolean }>>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10
    });
    const [tableData, setTableData] = useState<Forum[]>([]);
    const [rowCount, setRowCount] = useState(0);
    const navigate = useNavigate();
    const session = useSession();
    const isSmlDisplaySize = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("sm")
    );
    const isMedDisplaySize = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md")
    );

    const interfaceName = "forumsIndex"

    const dispatch = useAttorneyHubDispatch();
    const forumQuery = getForumQueryByInterface(
        interfaceName
    );
    const forumsResponse = getForumsByQuery(forumQuery);
    const queryIsLoading = getIsLoadingForumsByQuery(forumQuery);
    const errorLoadingQuery = getErrorLoadingForumsByQuery(forumQuery);

    const defaultQuery = new QueryForumsApiRequest(
        new ForumFilterParameters(),
        new ForumOrderParameters(
            ForumOrderField.CreatedDate,
            OrderDirection.Descending
        ),
        new PaginationParameters(
            pagination.pageIndex,
            pagination.pageSize
        )
    );

    useEffect(
        () => {
            if (!forumQuery) {
                dispatch(updateForumInterfaceQuery({ interfaceName, query: defaultQuery }));
                return;
            }

            if (forumQuery && forumsResponse === undefined && !queryIsLoading) {
                dispatch(
                    populateForumsByQuery({
                        session: session,
                        query: forumQuery,
                    })
                );
            } else if (forumsResponse !== undefined) {
                setTableData(forumsResponse);
                setRowCount(forumsResponse.length);
                setIsLoading(false);
            }
        },
        [
            forumsResponse,
            forumQuery,
            globalFilter
        ]
    );

    useEffect(
        () => {
            if (queryIsLoading && !isLoading && !showProgressBars) {
                setShowProgressBars(true);
            } else {
                setShowProgressBars(false);
            }
        },
        [queryIsLoading]
    );

    useEffect(
        () => {
            const filterParameters = new ForumFilterParameters();
            filterParameters.name = columnFilters.find((f) => f.id === "conversationName")?.value as string;

            let orderField = ForumOrderField.CreatedDate;
            let orderDir = OrderDirection.Descending;
            if (sorting.length > 0) {
                const sort = sorting[0];
                orderField = sort.id as ForumOrderField;
                orderDir = sort.desc ? OrderDirection.Descending : OrderDirection.Ascending;
            }
            const orderParameters = new ForumOrderParameters(orderField, orderDir);
            const newQuery = new QueryForumsApiRequest(
                filterParameters,
                orderParameters,
                new PaginationParameters(
                    pagination.pageIndex,
                    pagination.pageSize
                ),
                globalFilter
            );
            if (newQuery.isEqualTo(forumQuery)) {
                return;
            }
            dispatch(
                updateForumInterfaceQuery({
                    interfaceName: "forumsIndex",
                    query: newQuery,
                })
            );
        },
        [columnFilters, globalFilter, pagination, sorting]
    );

    useEffect(
        () => {
            if (errorLoadingQuery) {
                setShowAlertBanner(true);
                setShowSkeletons(true);
                setShowProgressBars(false);
                setIsLoading(false);
                setTableData([]);
            } else {
                setShowAlertBanner(false);
                setShowSkeletons(false);
            }
        },
        [errorLoadingQuery]
    );

    async function navigateToTarget(forum: Forum) {
        if (forum?.id) {
            navigate(`/communications/forums/${forum.id}`);
        }
    }

    function renderConversationNameColumn(forum: Forum): React.ReactNode {
        return (
            <CategoryItem dense>
                <ListItemIcon>
                    <MessageIcon />
                </ListItemIcon>
                <ListItemText
                    primary={forum?.name ?? ""}
                    slotProps={{
                        primary: { fontWeight: "500" }
                    }}
                />
            </CategoryItem>
        );
    }

    function renderCreatedDateColumn(
        forum: Forum
    ): React.ReactNode {
        if (!forum.createdDate) return null;
        return (
            <time>
                {moment(forum.createdDate).format("MM/DD/YY hh:mm A")}
            </time>
        );
    }

    const columnHelper = createMRTColumnHelper<Forum>();
    const columns = [];

    columns.push(
        ...[
            columnHelper.accessor(
                renderConversationNameColumn,
                {
                    header: "Conversation Name",
                    id: "name",
                    enableSorting: true,
                    enableColumnFilter: true
                }
            ),
            columnHelper.accessor(
                renderCreatedDateColumn,
                {
                    header: "Created",
                    id: "createdDate",
                }
            )
        ]);

    function getRowClickTheme(forum: Forum): SxProps<Theme> | undefined {
        if (forum.isResolved) {
            return {
                cursor: "pointer",
                backgroundColor: "rgba(0, 0, 0, 0.04)"
            };
        }
        return {
            cursor: "pointer",
            backgroundColor: "inherit"
        };
    }

    function renderForumRowActions(forum: Forum): React.ReactNode {
        return (
            <RowActionsContainer>
                <Tooltip title="View Conversation">
                    <IconButton
                        color="primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/communications/forums/${forum.id}`)
                        }}
                        size="small"
                    >
                        <PageviewIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Resolve">
                    <IconButton
                        color="secondary"
                        onClick={(e) => {
                            e.stopPropagation();

                        }}
                        size="small"
                    >
                        <ResolveIcon />
                    </IconButton>
                </Tooltip>
            </RowActionsContainer>
        );
    }

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        rowCount: rowCount,
        enableRowSelection: false,
        enableRowActions: true,
        enableTableHead: true,
        renderRowActions: ({ row }) => renderForumRowActions(row.original),
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => { void navigateToTarget(row.original); },
            sx: getRowClickTheme(row.original),
        }),
        muiTableProps: () => ({
            sx: {
                height: forumsResponse?.length === 0 ? "100%" : "initial",
            },
        }),
        renderToolbarInternalActions: ({ table }) => (
            <>
                {!isMedDisplaySize && (
                    <ToolbarButtonsContainer>
                        <ToolbarButtons>
                            <ToggleGlobalFilterButton table={table} />
                            <ToggleFiltersButton table={table} />
                            <ShowHideColumnsButton table={table} />
                            <ToggleDensePaddingButton table={table} />
                            <ToggleFullScreenButton table={table} />
                        </ToolbarButtons>
                        <ToolbarActionButtons>
                            <Button
                                color="primary"
                                startIcon={<AddCommentIcon />}
                                onClick={() => navigate("/communications/forums/compose")}
                            >
                                Compose Message
                            </Button>
                            <Button
                                color="primary"
                                startIcon={<ForumIcon />}
                                onClick={() => navigate("/communications/forums/create")}
                            >
                                New Conversation
                            </Button>
                        </ToolbarActionButtons>
                    </ToolbarButtonsContainer>
                )}
                {isMedDisplaySize && !isSmlDisplaySize && (
                    <>
                        <ComposeMessageButton
                            color="primary"
                            onClick={() => navigate("/communications/forums/compose")}
                        >
                            <AddCommentIcon fontSize="large" />
                        </ComposeMessageButton>
                        <NewForumButton
                            color="primary"
                            onClick={() => navigate("/communications/forums/create")}
                        >
                            <ForumIcon fontSize="large" />
                        </NewForumButton>
                    </>
                )}
            </>
        ),
        renderTopToolbarCustomActions: renderTopToolbarCustomActions,
        renderEmptyRowsFallback: () => {
            if (isLoading) {
                return;
            }
            return (
                <NoRowsContainer>
                    <AllCaughtUp alt="All Caught Up" src={EmptyInboxPlaceholder} />
                </NoRowsContainer>
            );
        },
        getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
        initialState: {
            columnPinning: { right: ["mrt-row-actions"] },
            columnOrder: [
                "name",
                "createdDate",
                "actions"
            ],
        },
        manualFiltering: true,
        enableStickyHeader: true,
        enableStickyFooter: true,
        enableColumnPinning: true,
        manualPagination: true,
        manualSorting: true,
        muiTableContainerProps: ({ table }) => ({
            sx: {
                height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
            },
        }),
        muiTablePaperProps: {
            sx: {
                height: "100%",
            },
        },
        muiToolbarAlertBannerProps: showAlertBanner
            ? {
                color: "error",
                title: errorLoadingQuery || ""
            }
            : {
                action: (
                    <AlertActions />
                ),
            },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner,
            showProgressBars,
            sorting,
            rowSelection,
            showSkeletons
        },
    });

    if (!session.user?.companyEntityId) {
        return (<NotAuthorized />);
    }

    return <MaterialReactTable table={table} />;
}
